import { useEffect, useRef, useState } from "react"
import {
	NodeViewContent,
	NodeViewWrapper,
	ReactNodeViewRenderer,
} from "@tiptap/react"

import type { MermaidTheme } from "@productlane/lib/src/tiptap/extensions/mintlify/mermaid-render"
import { MermaidExtension } from "@productlane/lib/src/tiptap/extensions/mintlify/mermaid"
import {
	currentMermaidTheme,
	renderMermaidSvg,
} from "@productlane/lib/src/tiptap/extensions/mintlify/mermaid-render"

import type { NodeViewProps } from "@tiptap/react"

const PREVIEW_DELAY_MS = 400

const OS_DARK = "(prefers-color-scheme: dark)"

// The dashboard follows the OS theme and sets no .dark class, unlike the portal.
function dashboardTheme(): MermaidTheme {
	const root = document.documentElement
	if (root.classList.contains("light")) return "light"
	if (window.matchMedia(OS_DARK).matches) return "dark"
	return currentMermaidTheme()
}

function useTheme(): MermaidTheme {
	const [theme, setTheme] = useState<MermaidTheme>(dashboardTheme)
	useEffect(() => {
		const update = () => setTheme(dashboardTheme())
		const observer = new MutationObserver(update)
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ["class"],
		})
		const media = window.matchMedia(OS_DARK)
		media.addEventListener("change", update)
		return () => {
			observer.disconnect()
			media.removeEventListener("change", update)
		}
	}, [])
	return theme
}

function MermaidNodeView({ node }: NodeViewProps) {
	const source = node.textContent.trim()
	const theme = useTheme()
	const previewRef = useRef<HTMLDivElement>(null)
	const [state, setState] = useState<"empty" | "rendered" | "error">("empty")

	useEffect(() => {
		if (!source) {
			setState("empty")
			return
		}
		let cancelled = false
		// Typing redraws once the author pauses, not on every key.
		const timer = setTimeout(() => {
			renderMermaidSvg(source, theme)
				.then((svg) => {
					if (cancelled || !previewRef.current) return
					previewRef.current.innerHTML = svg
					setState("rendered")
				})
				.catch(() => {
					if (!cancelled)
						setState((prev) => (prev === "empty" ? "empty" : "error"))
				})
		}, PREVIEW_DELAY_MS)
		return () => {
			cancelled = true
			clearTimeout(timer)
		}
	}, [source, theme])

	return (
		<NodeViewWrapper
			data-type="mermaid"
			className="pl-mermaid"
			data-placement={node.attrs.placement ?? undefined}
		>
			<NodeViewContent
				as="pre"
				className="pl-mermaid-source"
				data-mermaid-source=""
				spellCheck={false}
			/>
			<div
				ref={previewRef}
				contentEditable={false}
				className="pl-mermaid-preview"
				data-state={state}
				hidden={state === "empty"}
			/>
		</NodeViewWrapper>
	)
}

export const MermaidWithNodeView = MermaidExtension.extend({
	addNodeView() {
		return ReactNodeViewRenderer(MermaidNodeView)
	},
})
